import { AccentVariantEnum } from '@delmonicos/shared-design-system-react/src/components/accent-icon/accent-icon';
import {
  AutocompleteInputComponent,
  AutocompleteItem,
} from '@delmonicos/shared-design-system-react/src/components/autocomplete-input/autocomplete-input.component';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import { Divider } from '@delmonicos/shared-design-system-react/src/components/divider/divider';
import { Dropdown } from '@delmonicos/shared-design-system-react/src/components/dropdown/dropdown';
import { MultiTextInput } from '@delmonicos/shared-design-system-react/src/components/input/multi-text-input';
import { PasswordInput } from '@delmonicos/shared-design-system-react/src/components/input/password-input';
import { TextInput } from '@delmonicos/shared-design-system-react/src/components/input/text-input';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { Text } from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { HeadingEnum } from '@delmonicos/shared-design-system-react/src/models/enums/heading.enum';
import { OcppAuthenticationLayer } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/enums/authentication/ocpp-authentication-layer.enum';
import { useCreateChargePointFormValidation } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/create-charge-point.form-validation';
import { CreateChargePointModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/create-charge-point.model';
import { ChargePointIsLocked } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/outputs/charge-point/charge-point-is-locked.enum';
import { useEffect, useState } from 'react';
import {
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  useFormContext,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useCpmsBoGetNetworksPaginatedQuery } from '@app/core/service/cpms-bo-network.service';
import { useCustomerAutocomplete } from '@app/feature/customer/use-customer-autocomplete';

// TODO: Should be moved in SDK
const isIpWithRegex = (ip: string) => {
  const regex = new RegExp(
    '^' +
      '((25[0-5]|2[0-4]\\d|[01]?\\d\\d?)\\.)' +
      '{3}(25[0-5]|2[0-4]\\d|[01]?\\d\\d?)$',
  );
  return regex.test(ip);
};

const ChargePointCreateFormStep1CustomerSelection = ({
  field,
  fieldState: { error },
}: {
  field: ControllerRenderProps<CreateChargePointModel, 'customerId'>;
  fieldState: ControllerFieldState;
}) => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const { customerId } = useCreateChargePointFormValidation();

  // DATA FETCHING /////////////////////////////////////////////////////////////

  const { search, setSearch, selectedItem, autocompleteItems } =
    useCustomerAutocomplete(field.value);

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <AutocompleteInputComponent
      label={t('delmonicos-shared-sdk.outputs-gateway-charge-point.customer')}
      placeholder={t(
        'delmonicos-shared-sdk.outputs-gateway-charge-point.customer',
      )}
      required={!!customerId.required}
      value={search}
      onChange={setSearch}
      onSearch={setSearch}
      items={autocompleteItems}
      selectedItem={selectedItem}
      onSelect={(selectedItemNext) => {
        field.onChange(selectedItemNext?.key ?? '');
      }}
      emptyResultText={t('global.no-result-found')}
      errorMessage={error?.message}
    />
  );
};

const ChargePointCreateFormStep1NetworkSelection = ({
  field,
  fieldState: { error },
}: {
  field: ControllerRenderProps<CreateChargePointModel, 'networkId'>;
  fieldState: ControllerFieldState;
}) => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const { watch } = useFormContext<CreateChargePointModel>();
  const { networkId } = useCreateChargePointFormValidation();

  // WATCHERS ////////////////////////////////////////////////////////////////

  const customerId = watch('customerId');

  useEffect(() => {
    if (field.value && !customerId) {
      field.onChange('');
    }
  }, [customerId, field]);

  // SEARCH TERM ///////////////////////////////////////////////////////////////

  const [search, setSearch] = useState<string>('');

  // DATA FETCHING /////////////////////////////////////////////////////////////

  const { data, isSuccess } = useCpmsBoGetNetworksPaginatedQuery({
    limit: 10,
    offset: 0,
    customerId,
    name: search === '' ? undefined : `~${search}`,
  });

  // AUTOCOMPLETE ITEM /////////////////////////////////////////////////////////

  const [selectedItem, setSelectedItem] = useState<AutocompleteItem | null>(
    null,
  );

  useEffect(() => {
    if (isSuccess) {
      const network = data.docs.find(({ id }) => id === field.value) ?? null;

      if (network) {
        setSelectedItem({ key: network.id, label: network.name });
        return;
      }

      setSelectedItem(null);
    }
  }, [search, data, isSuccess, field.value]);

  // AUTOCOMPLETE ITEMS ////////////////////////////////////////////////////////

  const [autocompleteItems, setAutocompleteItems] = useState<
    AutocompleteItem[]
  >([]);

  useEffect(() => {
    if (isSuccess) {
      setAutocompleteItems(
        data.docs.map(({ id, name }) => ({ key: id, label: name })) ?? [],
      );
    }
  }, [search, data, isSuccess]);

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <AutocompleteInputComponent
      label={t('delmonicos-shared-sdk.outputs-gateway-charge-point.network')}
      placeholder={t(
        'delmonicos-shared-sdk.outputs-gateway-charge-point.network',
      )}
      required={!!networkId.required}
      disabled={field.disabled || !customerId}
      value={search}
      onChange={setSearch}
      onSearch={setSearch}
      items={autocompleteItems}
      selectedItem={selectedItem}
      onSelect={(selectedItemNext) => {
        field.onChange(selectedItemNext?.key ?? '');
      }}
      emptyResultText={t('global.no-result-found')}
      errorMessage={error?.message}
    />
  );
};

const ChargePointCreateFormStep1AuthorizedIpAddressesSelection = ({
  field,
  fieldState: { error },
}: {
  field: ControllerRenderProps<CreateChargePointModel, 'authorizedIps'>;
  fieldState: ControllerFieldState;
}) => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();

  // INPUT PROPERTIES //////////////////////////////////////////////////////////

  const [value, setValue] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  // ITEM LIST /////////////////////////////////////////////////////////////////

  const [itemList, setItemList] = useState<string[]>(field.value ?? []);

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleChange = (itemListChanged: string[]) => {
    setItemList(itemListChanged);
    field.onChange(itemListChanged);
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <Stack gap={GapEnum.XS} direction="column">
      <Text>
        {t('delmonicos-shared-sdk.outputs-charge-point.authorized-ips')}
      </Text>
      <MultiTextInput
        placeholder={t(
          'delmonicos-shared-sdk.outputs-charge-point.authorized-ips',
        )}
        inputValue={value}
        items={itemList}
        onAddItem={(item) => {
          if (item === '') {
            setErrorMessage('');
            return;
          }

          if (itemList.includes(item)) {
            setErrorMessage(
              t('$.charge-point-create.step-1.authorizations.duplicate'),
            );
            return;
          }

          if (!isIpWithRegex(item)) {
            setErrorMessage(
              t('$.charge-point-create.step-1.authorizations.invalid'),
            );
            return;
          }

          handleChange([...itemList, item]);

          setValue('');
          setErrorMessage('');
        }}
        onRemoveItem={(index) => {
          handleChange(itemList.filter((_, i) => i !== index));
        }}
        onInputChange={setValue}
        errorMessage={errorMessage || error?.message}
      />
    </Stack>
  );
};

const ChargePointCreateFormStep1InformationFragment = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const { control } = useFormContext<CreateChargePointModel>();
  const { identity, name, customerId, networkId } =
    useCreateChargePointFormValidation();

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <Card flex={1}>
      <Stack direction="column" gap={GapEnum.L}>
        <AccentTitle
          as={HeadingEnum.H2}
          icon="info-circle"
          text={t('$.charge-point-create.step-1.information.title')}
          variant={AccentVariantEnum.Primary}
        />
        <Controller
          name="identity"
          control={control}
          rules={identity}
          render={({ field, fieldState: { error } }) => {
            return (
              <TextInput
                label={t('delmonicos-shared-sdk.outputs-charge-point.identity')}
                placeholder={t(
                  'delmonicos-shared-sdk.outputs-charge-point.identity',
                )}
                required={!!identity.required}
                errorMessage={error?.message}
                {...field}
                value={field.value ?? ''}
                onChange={(e) => {
                  field.onChange(e.target.value === '' ? '' : e.target.value);
                }}
              />
            );
          }}
        />
        <Controller
          name="name"
          control={control}
          rules={name}
          render={({ field, fieldState: { error } }) => {
            return (
              <TextInput
                label={t('delmonicos-shared-sdk.outputs-charge-point.name')}
                placeholder={t(
                  'delmonicos-shared-sdk.outputs-charge-point.name',
                )}
                required={!!name.required}
                disabled={field.disabled}
                errorMessage={error?.message}
                {...field}
                value={field.value ?? ''}
                onChange={(e) => {
                  field.onChange(e.target.value === '' ? '' : e.target.value);
                }}
              />
            );
          }}
        />
        <Controller
          name="customerId"
          control={control}
          rules={customerId}
          render={ChargePointCreateFormStep1CustomerSelection}
        />
        <Controller
          name="networkId"
          control={control}
          rules={networkId}
          render={ChargePointCreateFormStep1NetworkSelection}
        />
      </Stack>
    </Card>
  );
};

const ChargePointCreateFormStep1IsLockedFragment = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const { control } = useFormContext<CreateChargePointModel>();
  const { isLocked } = useCreateChargePointFormValidation();

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <Card direction="column" gap={GapEnum.L}>
      <AccentTitle
        as={HeadingEnum.H2}
        icon="info-circle"
        text={t('delmonicos-shared-sdk.outputs-charge-point.is-locked')}
        variant={AccentVariantEnum.Accent1}
      />
      <Controller
        name="isLocked"
        control={control}
        rules={isLocked}
        render={({ field, fieldState: { error } }) => {
          return (
            <Stack gap={GapEnum.XS} direction="column">
              <Dropdown
                label={t(
                  'delmonicos-shared-sdk.outputs-charge-point.is-locked',
                )}
                placeholder={t(
                  'delmonicos-shared-sdk.outputs-charge-point.is-locked',
                )}
                required={!!isLocked.required}
                removable
                errorMessage={error?.message}
                {...field}
                value={
                  field.value === undefined
                    ? ''
                    : field.value
                      ? ChargePointIsLocked.TRUE
                      : ChargePointIsLocked.FALSE
                }
                onChange={(value) => {
                  if (value === undefined) {
                    field.onChange('');
                  } else {
                    field.onChange(value === ChargePointIsLocked.TRUE);
                  }
                }}>
                {Object.values(ChargePointIsLocked).map((s) => (
                  <Dropdown.Option key={s} value={s}>
                    {t(
                      `delmonicos-shared-sdk.outputs-charge-point-is-locked.${s}`,
                    )}
                  </Dropdown.Option>
                ))}
              </Dropdown>
            </Stack>
          );
        }}
      />
    </Card>
  );
};

const ChargePointCreateFormStep1AuthenticationFragment =
  (): React.JSX.Element => {
    // CONTEXT /////////////////////////////////////////////////////////////////

    const { t } = useTranslation();
    const { control, watch } = useFormContext<CreateChargePointModel>();
    const { authenticationLayer, authorizedIps, ocppAuthenticationPassword } =
      useCreateChargePointFormValidation();

    // WATCHERS ////////////////////////////////////////////////////////////////

    const authenticationLayerSelected = watch('authenticationLayer');

    // RENDER //////////////////////////////////////////////////////////////////

    return (
      <Card gap={GapEnum.L} flex={1}>
        <AccentTitle
          as={HeadingEnum.H2}
          icon="log-in-02"
          text={t(
            'delmonicos-shared-sdk.outputs-charge-point.authentication-layer',
          )}
          variant={AccentVariantEnum.Primary}
        />
        <Controller
          name="authenticationLayer"
          control={control}
          rules={authenticationLayer}
          render={({ field, fieldState: { error } }) => (
            <Stack gap={GapEnum.XS} direction="column">
              <Dropdown
                label={t(
                  'delmonicos-shared-sdk.outputs-charge-point.authentication-layer',
                )}
                placeholder={t(
                  'delmonicos-shared-sdk.outputs-charge-point.authentication-layer',
                )}
                required={!!authenticationLayer.required}
                removable
                errorMessage={error?.message}
                {...field}
                onChange={(value) => field.onChange(value)}>
                {Object.values(OcppAuthenticationLayer).map((s) => (
                  <Dropdown.Option key={s} value={s}>
                    {t(
                      `delmonicos-shared-sdk.enums-ocpp-authentication-layer.${s}`,
                    )}
                  </Dropdown.Option>
                ))}
              </Dropdown>
            </Stack>
          )}
        />
        {(authenticationLayerSelected ===
          OcppAuthenticationLayer.HTTP_BASIC_TLS ||
          authenticationLayerSelected ===
            OcppAuthenticationLayer.HTTP_BASIC_NO_TLS) && (
          <Controller
            name="ocppAuthenticationPassword"
            control={control}
            rules={ocppAuthenticationPassword}
            render={({ field, fieldState: { error } }) => {
              return (
                <PasswordInput
                  label={t(
                    'delmonicos-shared-sdk.inputs-create-charge-point.ocpp-authentication-password',
                  )}
                  placeholder={t(
                    'delmonicos-shared-sdk.inputs-create-charge-point.ocpp-authentication-password',
                  )}
                  required={!!ocppAuthenticationPassword.required}
                  errorMessage={error?.message}
                  {...field}
                  value={field.value ?? ''}
                  onChange={(e) => {
                    field.onChange(e.target.value === '' ? '' : e.target.value);
                  }}
                />
              );
            }}
          />
        )}
        <Divider />
        <Controller
          name="authorizedIps"
          control={control}
          rules={authorizedIps}
          render={ChargePointCreateFormStep1AuthorizedIpAddressesSelection}
        />
      </Card>
    );
  };

export const ChargePointCreateFormStep1Controller = (): React.JSX.Element => {
  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <Stack
      direction="row"
      justifyContent="stretch"
      alignItems="stretch"
      flex={1}
      gap={GapEnum.XL}>
      <Stack direction="column" flex={1} alignItems="stretch" gap={GapEnum.XL}>
        <ChargePointCreateFormStep1InformationFragment />
        <ChargePointCreateFormStep1IsLockedFragment />
      </Stack>
      <Stack direction="column" flex={1} alignItems="stretch" gap={GapEnum.XL}>
        <ChargePointCreateFormStep1AuthenticationFragment />
      </Stack>
    </Stack>
  );
};
